import { VideoQuality } from '@zoom/videosdk';
import { useCallback, useEffect, useState } from 'react';

function useZoomVideoSdk({
  zoomClient,
  mediaStream,
  participantVideoContainerId,
  selfVideoContainerId,
}) {
  const [isCameraOff, setIsCameraOff] = useState(false);

  const getCurrentUserId = useCallback(
    () => zoomClient?.getCurrentUserInfo()?.userId,
    [zoomClient],
  );

  const startVideo = useCallback(
    async deviceId => {
      const currentUserId = getCurrentUserId();
      const videoElement = document.getElementById(selfVideoContainerId);
      await mediaStream.startVideo({ cameraId: deviceId });
      mediaStream.attachVideo(currentUserId, VideoQuality.Video_360P).then(video => {
        videoElement.innerHTML = '';
        videoElement.appendChild(video);
        setIsCameraOff(false);
      });
    },
    [mediaStream, getCurrentUserId, selfVideoContainerId],
  );

  const peerVideoStateChange = useCallback(
    payload => {
      const videoContainerElement = document.getElementById(participantVideoContainerId);
      if (payload.action === 'Start') {
        zoomClient.getAllUser().forEach(user => {
          if (
            user.bVideoOn &&
            user.userId !== getCurrentUserId() &&
            user.userIdentity !== zoomClient?.getCurrentUserInfo()?.userIdentity
          ) {
            mediaStream.attachVideo(user.userId, VideoQuality.Video_360P).then(video => {
              videoContainerElement.innerHTML = '';
              videoContainerElement.appendChild(video);
            });
          }
        });
      }
      if (payload.action === 'Stop') {
        mediaStream.detachVideo(payload.userId);
        videoContainerElement.innerHTML = '';
      }
    },
    [getCurrentUserId, participantVideoContainerId, zoomClient, mediaStream],
  );

  const stopVideo = useCallback(async () => {
    const videoElement = document.getElementById(selfVideoContainerId);
    const currentUserId = zoomClient?.getCurrentUserInfo()?.userId;
    await mediaStream.stopVideo();
    await mediaStream.detachVideo(currentUserId);
    videoElement.innerHTML = '';
    setIsCameraOff(true);
  }, [mediaStream, selfVideoContainerId, zoomClient]);

  useEffect(() => {
    if (!zoomClient) return;
    zoomClient.on('peer-video-state-change', peerVideoStateChange);
    return () => {
      zoomClient.off('peer-video-state-change', peerVideoStateChange);
    };
  }, [peerVideoStateChange, zoomClient]);

  return {
    isCameraOff,
    setIsCameraOff,
    stopVideo,
    startVideo,
  };
}

export default useZoomVideoSdk;
